import { motion } from 'framer-motion';
import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifyCode, storeTokens, sendVerificationCode } from '@/services/authService';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import OTPInput from '@/components/ui/OTPInput';
import BackArrow from '@/components/ui/BackArrow';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { cn } from '@/utils/cn';
import { formatTime } from '@/utils/otpUtils';
import { useOTPTimer } from '@/hooks/useOTPTimer';
import { pageContainer, pageItem } from '@/utils/animations';
import { clearHistoryStack } from '@/utils/navigationHistory';
import { analyticsService } from '@/services/analyticsService';

interface OTPVerificationPageProps {
  className?: string;
}

const OTPVerificationPage = ({ className }: OTPVerificationPageProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshAuthState } = useAuth();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const verifyInFlight = useRef(false);

  // Get phone_number from navigation state (login flow uses phone)
  const phone_number = location.state?.phone_number || '';

  // Use OTP timer hook with 60 seconds (1 minute)
  const { timeLeft, canResend, resetTimer } = useOTPTimer({ initialTime: 60 });

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
  };

  const handleOTPChange = (value: string) => {
    setOtp(value);
    
    // Clear error when user starts typing
    if (error && value.trim()) {
      setError('');
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      setError('لطفاً کد ۶ رقمی را کامل وارد کنید');
      return;
    }

    if (!phone_number) {
      setError('شماره همراه یافت نشد. لطفاً دوباره تلاش کنید.');
      return;
    }

    if (verifyInFlight.current) return;
    verifyInFlight.current = true;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');
    void analyticsService.logEvent('login_otp_submitted');

    try {
      console.log('🔐 [OTPVerification] Verifying code for login:', { phone_number, code: otp });
      
      // Verify OTP code
      const response = await verifyCode(phone_number, otp);
      
      console.log('✅ [OTPVerification] Code verified successfully:', response);
      
      if (response.status === 'success' && response.action === 'login') {
        // Show success message
        setSuccessMessage('ایول وارد شدی');
        
        // Store tokens - BOTH should be in response body now
        if (response.access_token && response.refresh_token) {
          console.log('💾 [OTPVerification] Storing tokens:', {
            hasAccessToken: !!response.access_token,
            hasRefreshToken: !!response.refresh_token,
            expiresIn: response.expires_in,
            expiresInFromBackend: response.expires_in,
            willUse: response.expires_in || 300
          });
          
          await storeTokens(
            response.access_token,
            response.refresh_token,
            response.expires_in || 300  // 5 minutes for testing
          );
          await analyticsService.setUserIdFromAccessToken(response.access_token);
          
          console.log('✅ [OTPVerification] JWT tokens stored successfully');
        } else {
          console.error('❌ [OTPVerification] Missing tokens in response:', {
            hasAccessToken: !!response.access_token,
            hasRefreshToken: !!response.refresh_token
          });
          throw new Error('Tokens not received from server');
        }
        
        // Update auth context with user login
        login({
          phone_number: response.phone_number,
          authMethod: 'phone',
          is_guest: false,
          is_registered: true
        });
        
        // CRITICAL: Refresh auth state to get updated profile from server
        try {
          await refreshAuthState();
          console.log('✅ [OTPVerification] Auth state refreshed from server');
        } catch (e) {
          console.warn('⚠️ [OTPVerification] Failed to refresh auth state', e);
        }

        void analyticsService.logEvent('login_success');

        // Refresh avatar from server before navigating
        try {
          const refreshAvatar = (window as any).refreshAvatar;
          if (typeof refreshAvatar === 'function') {
            await refreshAvatar();
            console.log('🖼️ [OTPVerification] Avatar refreshed after login');
          }
        } catch (e) {
          console.warn('⚠️ [OTPVerification] Failed to refresh avatar after login', e);
        }

        console.log('🏠 [OTPVerification] Navigating to dashboard');
        
        // Clear any onboarding data from sessionStorage
        sessionStorage.removeItem('onboarding_topic_id');
        sessionStorage.removeItem('onboarding_topic_name');
        sessionStorage.removeItem('onboarding_phone_number');
        sessionStorage.removeItem('onboarding_name');
        sessionStorage.removeItem('skip_books_selection');
        sessionStorage.removeItem('xp_claim_state');
        sessionStorage.removeItem('achievement_state');
        
        // پاک کردن history stack و رفتن به dashboard
        clearHistoryStack('/dashboard');
        navigate('/dashboard', { replace: true });
      } else if (response.status === 'success' && response.action === 'register') {
        // User needs to register first
        setError('همچین شماره‌ای نیست. برو ثبت نام کن اول');
      } else {
        throw new Error(response.message || 'خطا در تایید کد');
      }
    } catch (error) {
      console.error('❌ [OTPVerification] Error verifying code:', error);
      setError(error instanceof Error ? error.message : 'کد وارد شده صحیح نیست. لطفاً دوباره تلاش کنید.');
    } finally {
      verifyInFlight.current = false;
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend || !phone_number) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('📧 [OTPVerification] Resending verification code to:', phone_number);
      
      // Resend verification code
      await sendVerificationCode(phone_number);
      
      console.log('✅ [OTPVerification] Code resent successfully');
      
      // Reset timer and clear OTP
      resetTimer();
      setOtp('');
      
      setSuccessMessage('کد رو فرستادم دوباره');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('❌ [OTPVerification] Error resending code:', error);
      setError(error instanceof Error ? error.message : 'خطا در ارسال مجدد کد. لطفاً دوباره تلاش کنید.');
    } finally {
      setIsLoading(false);
    }
  };



  const handleBack = () => {
    navigate('/login');
  };

  return (
    <motion.div
      className={cn(
        'min-h-screen flex flex-col bg-white',
        'px-7 py-12',
        'relative overflow-hidden',
        className
      )}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {/* Back arrow in top-right corner for RTL */}
      <motion.div
        className="absolute top-6 right-6 z-10"
        variants={pageItem}
      >
        <BackArrow onClick={handleBack} variant="simple" />
      </motion.div>

      {/* Main content container */}
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-auth-mobile md:max-w-auth-tablet lg:max-w-auth-desktop xl:max-w-auth-desktop-lg mx-auto translate-y-14">

        {/* Instruction text */}
        <motion.div
          className="text-center mb-8"
          variants={pageItem}
        >
          <p className={cn(
            'text-base lg:text-lg text-gray-600 leading-relaxed',
            'font-persian'
          )}>
            برای ورود به بوکا کدی که برات به شماره
          </p>
          <p className={cn(
            'text-lg lg:text-xl font-medium text-gray-800 mt-2',
            'font-persian'
          )}>
            {toPersianDigits(phone_number)}
          </p>
          <p className={cn(
            'text-base lg:text-lg text-gray-600 mt-1',
            'font-persian'
          )}>
            فرستادیم رو وارد کن
          </p>
        </motion.div>

        {/* OTP Input */}
        <motion.div
          className="w-full mb-4"
          variants={pageItem}
        >
          <OTPInput
            length={6}
            value={otp}
            onChange={handleOTPChange}
            error=""
            disabled={isLoading}
          />
        </motion.div>

        {/* Success/Error Messages - زیر اینپوت */}
        {(successMessage || error) && (
          <motion.div
            className="mb-6 text-center"
            variants={pageItem}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {successMessage && (
              <p className="text-green-600 text-sm font-persian">
                {successMessage}
              </p>
            )}
            {error && (
              <div className="space-y-2">
                <p className="text-red-600 text-sm font-persian">
                  {error}
                </p>
                {error === 'همچین شماره‌ای نیست. برو ثبت نام کن اول' && (
                  <div className="text-center">
                    <span className="text-muted font-persian text-sm">
                      کاربر بوکا نیستی؟{' '}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate('/welcome')}
                      className="text-primary-500 font-persian text-sm font-medium hover:text-primary-600 transition-colors"
                    >
                      ثبت نام
                    </button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Resend timer */}
        <motion.div
          className="text-center mb-6"
          variants={pageItem}
        >
          {!canResend ? (
            <p className={cn(
              'text-sm text-gray-500',
              'font-persian'
            )}>
              ارسال مجدد کد تا {toPersianDigits(formatTime(timeLeft))} ثانیه دیگر
            </p>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResendCode}
              disabled={isLoading}
              className="font-persian"
            >
              ارسال مجدد کد
            </Button>
          )}
        </motion.div>

        {/* Verify Button */}
        <motion.div
          className="w-full"
          variants={pageItem}
        >
          <Button
            variant="primary"
            size="md"
            className="w-full rounded-xl"
            onClick={handleVerify}
            disabled={isLoading || otp.length !== 6}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <LoadingSpinner size="sm" />
                <span>در حال تأیید...</span>
              </div>
            ) : (
              'ورود'
            )}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default OTPVerificationPage;
